(() => {
  if (customElements.get('shop-tv-hero')) return;

  class ShopTvHero extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.slides = Array.from(this.querySelectorAll('[data-tv-slide]'));
      this.controls = this.querySelector('[data-tv-hero-controls]');
      this.dots = Array.from(this.querySelectorAll('[data-tv-hero-to]'));
      this.status = this.querySelector('[data-tv-hero-status]');
      this.pointer = null;
      this.swipeClick = null;
      if (this.controls) this.controls.hidden = this.slides.length < 2;
      if (!this.slides.length) return;

      const options = { signal: this.controller.signal };
      this.addEventListener('click', event => this.onClick(event), { ...options, capture: true });
      this.addEventListener('keydown', event => this.onKeydown(event), options);
      this.addEventListener('pointerdown', event => this.onPointerDown(event), { ...options, passive: true });
      window.addEventListener('pointerup', event => this.onPointerUp(event), { ...options, passive: true });
      window.addEventListener('pointercancel', () => { this.pointer = null; }, { ...options, passive: true });
      document.addEventListener('shopify:block:select', event => {
        if (window.Shopify?.designMode !== true || !event.detail?.blockId) return;
        const index = this.slides.findIndex(slide => slide.dataset.tvBlockId === String(event.detail.blockId));
        if (index >= 0) this.show(index);
      }, options);

      const visibleIndex = this.slides.findIndex(slide => !slide.hidden);
      this.show(Number.isInteger(this.index) ? this.index : Math.max(visibleIndex, 0));
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.pointer = null;
      this.swipeClick = null;
    }

    onClick(event) {
      if (this.swipeClick && event.detail > 0) {
        const swipeClick = this.swipeClick;
        this.swipeClick = null;
        if (performance.now() <= swipeClick.expires && (!('pointerId' in event) || event.pointerId === swipeClick.id)) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
      }
      const action = event.target.closest('[data-tv-hero-prev], [data-tv-hero-next], [data-tv-hero-to]');
      if (!action || !this.controls?.contains(action)) return;
      event.preventDefault();
      if (action.hasAttribute('data-tv-hero-prev')) this.show(this.index - 1);
      else if (action.hasAttribute('data-tv-hero-next')) this.show(this.index + 1);
      else {
        const index = Number(action.dataset.tvHeroTo);
        if (Number.isInteger(index)) this.show(index);
      }
    }

    onKeydown(event) {
      if (!this.controls?.contains(event.target)) return;
      let index;
      if (event.key === 'ArrowLeft') index = this.index - 1;
      else if (event.key === 'ArrowRight') index = this.index + 1;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = this.slides.length - 1;
      else return;
      event.preventDefault();
      this.show(index);
    }

    onPointerDown(event) {
      this.pointer = null;
      this.swipeClick = null;
      if (this.slides.length < 2 || !event.isPrimary || event.button !== 0 || event.pointerType === 'mouse') return;
      const interactive = event.target.closest('button, a, input, select, textarea, summary, video, iframe, [role="button"], [contenteditable="true"]');
      if (interactive && !interactive.matches('[data-tv-hero-image-open]')) return;
      if (event.target.closest('[data-tv-hero-controls]')) return;
      this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
    }

    onPointerUp(event) {
      if (!this.pointer || this.pointer.id !== event.pointerId) return;
      const distanceX = event.clientX - this.pointer.x;
      const distanceY = event.clientY - this.pointer.y;
      this.pointer = null;
      if (Math.abs(distanceX) < 45 || Math.abs(distanceX) < Math.abs(distanceY) * 1.4) return;
      this.swipeClick = { id: event.pointerId, expires: performance.now() + 700 };
      this.show(this.index + (distanceX < 0 ? 1 : -1));
    }

    show(index) {
      if (!this.slides?.length || !Number.isInteger(index)) return;
      const next = ((index % this.slides.length) + this.slides.length) % this.slides.length;
      const focused = document.activeElement;
      const hidesFocus = this.slides.some((slide, position) => position !== next && slide.contains(focused));
      this.slides.forEach((slide, position) => {
        const hidden = position !== next;
        slide.hidden = hidden;
        slide.setAttribute('aria-hidden', String(hidden));
        slide.inert = hidden;
      });
      this.index = next;
      const previousId = this.slides[(next - 1 + this.slides.length) % this.slides.length].id;
      const nextId = this.slides[(next + 1) % this.slides.length].id;
      if (previousId) this.controls?.querySelector('[data-tv-hero-prev]')?.setAttribute('aria-controls', previousId);
      if (nextId) this.controls?.querySelector('[data-tv-hero-next]')?.setAttribute('aria-controls', nextId);
      this.dots?.forEach(dot => {
        const active = Number(dot.dataset.tvHeroTo) === next;
        dot.setAttribute('aria-disabled', String(active));
        dot.setAttribute('aria-current', String(active));
      });
      if (this.status) {
        this.status.textContent = (this.dataset.statusTemplate || '[current] / [total]')
          .replaceAll('[current]', String(next + 1))
          .replaceAll('[total]', String(this.slides.length));
      }
      if (hidesFocus) {
        const nextFocus = this.controls && !this.controls.hidden
          ? this.controls.querySelector('[data-tv-hero-next]') || this.dots[next] || this.controls.querySelector('[data-tv-hero-prev]')
          : null;
        if (nextFocus) nextFocus.focus({ preventScroll: true });
        else {
          if (!this.hasAttribute('tabindex')) this.setAttribute('tabindex', '-1');
          this.focus({ preventScroll: true });
        }
      }
    }
  }

  customElements.define('shop-tv-hero', ShopTvHero);
})();
